import clsx from 'clsx'
import React, { useCallback, useEffect, useState } from 'react'
import { Control, Controller, DeepMap, FieldError, useForm, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import Select from 'react-select'
import { CheckAviResponseType, RoomTypeData } from '../../../../services/booking/response/CheckAviResponse'
import { Button } from 'react-bootstrap'
import { z } from 'zod'
import useCheckAvi from '../../../../services/booking/hooks/useCheckAvi'
import { BookingV4 } from '../../../../services/create-booking-v4/schema/GetAllBookingResponseV4'
import { useUpdateBooking } from '../../../../services/booking/hooks/useUpdateBooking'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { UpdateBookingType } from '../../../../services/booking/schema/UpdateBookingSchema'

interface AssignRoomFormProps {
    reservation: BookingV4 | null,
    onClose: () => void
}

const AssignRoomZodSchema = z.object({
    room_type_id: z.string().nonempty('Room type is required'),
    room_id: z.string().nonempty('Room is required'),
    rate_plan_id: z.string().nonempty('Rate plan is required'),
})

type AssignRoomForm = z.infer<typeof AssignRoomZodSchema>

const AssignRoomFormComponent: React.FC<AssignRoomFormProps> = ({ reservation, onClose }) => {
    if (!reservation) {
        return null;
    }

    const { control, setValue, watch, formState: { errors }, setError, reset, handleSubmit } = useForm<AssignRoomForm>({})

    useEffect(() => {
        if (reservation) {
            console.log(reservation)
            reset({
                room_type_id: reservation.room_type_id ?? undefined,
                room_id: reservation.room_id ?? undefined,
                rate_plan_id: reservation.rate_plan_id ?? undefined,
            });
        }
    }, [reservation])

    const { data: roomAvi, isFetching, isError, error, isSuccess } = useCheckAvi({
        checkin_date: reservation.checkin_date,
        checkout_date: reservation.checkout_date,
        booking_id: reservation.id
    });

    const handleClose = () => {
        reset();
        onClose();
    }

    const { mutate, isPending } = useUpdateBooking();

    const queryClient = useQueryClient();

    const onSubmit = useCallback((values: AssignRoomForm) => {
        toast.loading('Assign Room...', {
            id: 'assign-room',
        })

        const payload: UpdateBookingType = {
            invoice_number: reservation.invoice_number,
            invoice_date: reservation.invoice_date,
            status: reservation.status,
            mark: reservation.mark,
            public_note: reservation.public_note,
            private_note: reservation.private_note,
            customer_phone: reservation.customer_phone,
            customer_address: reservation.customer_address,
            customer_country: reservation.customer_country,
            customer_state: reservation.customer_state,
            customer_city: reservation.customer_city,
            customer_zip: reservation.customer_zip,
            customer_date_of_birth: reservation.customer_date_of_birth,
            customer_identity_number: reservation.customer_identity_number,
            payment_type: reservation.payment_type ?? '',
            customer_name: reservation.customer_name,
            customer_surname: reservation.customer_surname ?? '',
            customer_email: reservation.customer_email ?? '',
            checkin_date: reservation.checkin_date,
            checkout_date: reservation.checkout_date,
            room_type_id: values.room_type_id,
            rate_plan_id: values.rate_plan_id,
            room_id: values.room_id,
            adults: reservation.adults.toString(),
            children: reservation.children.toString(),
            rates: reservation.rates.map(r => ({ ...r, rate: Number(r.rate) })),
        }

        mutate({ bookingId: reservation.id, formData: payload }, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['bookings', 'availability'].includes(key as string))
                });

                toast.success('Assign Room Success', {
                    id: 'assign-room',
                });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof AssignRoomForm, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('There are errors in the form please check and try again', { id: 'assign-room' });
                    }
                }
            },
        });

    }, [mutate])

    return (
        <>
            {isFetching && <div>Loading...</div>}
            {isError && <div>Error: {error.message}</div>}
            {isSuccess && roomAvi && (
                <form onSubmit={handleSubmit(onSubmit)}>
                    {/* Room Select */}
                    <SelectRoomType control={control} roomType={roomAvi} setValue={setValue} errors={errors} />
                    {/* Subroom it will be number of rooms Select */}
                    {watch(`room_type_id`) && (
                        <SubroomComponent watch={watch} control={control} setValue={setValue} roomType={roomAvi} errors={errors} />
                    )}
                    {/* Rateplans select */}
                    {watch(`room_type_id`) && (
                        <RateplansComponent control={control} watch={watch} roomType={roomAvi} errors={errors} />
                    )}
                    <Button className='mt-3' type='submit' disabled={isPending}>Submit</Button>
                </form>
            )}
        </>
    )
}

export default AssignRoomFormComponent

interface SelectRoomTypeProps {
    control: Control<any>
    roomType: CheckAviResponseType
    setValue: UseFormSetValue<any>
    errors: DeepMap<any, FieldError>
}

interface RoomType {
    value: string,
    label: string
}

const SelectRoomType: React.FC<SelectRoomTypeProps> = ({ control, roomType, setValue, errors }) => {
    const [roomTypeOptions, setRoomTypeOptions] = useState<RoomType[]>([]);

    useEffect(() => {
        if (roomType.data.length > 0) {
            setRoomTypeOptions(roomType.data.map((room: { id: string, name: string }) => ({
                value: room.id,
                label: room.name
            })))
        }
    }, [roomType])

    return (
        <div className="col-12">
            <label className="col-form-label pt-0" htmlFor="room">Room:</label>
            <Controller
                control={control}
                name={`room_type_id`}
                render={({ field }) => (
                    <Select
                        className={clsx('', { 'is-invalid': errors.room_type_id })}
                        options={roomTypeOptions}
                        value={roomTypeOptions.find((room) => room.value === field.value)}
                        onChange={(selectedOption) => {
                            if (selectedOption) {
                                field.onChange(selectedOption.value);
                                setValue(`room_id`, '');
                            }
                        }}
                        noOptionsMessage={() => 'No rooms available'}
                    />
                )}
            />
            {errors.room_type_id && (
                <div className="invalid-feedback">
                    {errors.room_type_id.message}
                </div>
            )}
        </div>
    )
}

interface SubroomComponentProps {
    watch: UseFormWatch<any>,
    control: Control<any>,
    setValue: UseFormSetValue<any>,
    roomType: CheckAviResponseType,
    errors: DeepMap<any, FieldError>
}

interface Subroom {
    value: string,
    label: string
}

const SubroomComponent: React.FC<SubroomComponentProps> = ({ watch, control, roomType, errors }) => {
    const [subroomOptions, setSubroomOptions] = useState<Subroom[]>([]);

    useEffect(() => {
        const roomTypeId = watch(`room_type_id`);
        if (roomTypeId) {
            const roomData = roomType.data.find((room: RoomTypeData) => room.id === roomTypeId);
            if (roomData) {
                let rooms = roomData.rooms?.map((room: { id: string, room_number: string }) => ({
                    value: room.id,
                    label: room.room_number
                })) || [];

                // Jika tidak ada room, tambahkan opsi "Unassigned Room"
                if (rooms.length === 0) {
                    rooms = [{ value: '', label: "Unassigned Room" }];
                }

                setSubroomOptions(rooms);
            }
        }
    }, [watch(`room_type_id`)]);

    return (
        <div className="col-12">
            <label className="col-form-label" htmlFor="subroom">Subroom:</label>
            <Controller
                control={control}
                name={`room_id`}
                render={({ field }) => (
                    <Select
                        className={clsx('', { 'is-invalid': errors.room_id })}
                        options={subroomOptions}
                        value={subroomOptions.find((room) => room.value === field.value) || null}
                        onChange={(selectedOption) => {
                            const newRoomId = selectedOption?.value || '';
                            field.onChange(newRoomId);
                        }}
                        noOptionsMessage={() => 'No rooms available'}
                    />
                )}
            />
            {errors?.room_id && (
                <div className="invalid-feedback">
                    {errors.room_id.message}
                </div>
            )}
        </div>
    );
};

interface RateplansComponentProps {
    control: Control<any>
    watch: UseFormWatch<any>
    roomType: CheckAviResponseType,
    errors: DeepMap<any, FieldError>
}

interface Rateplan {
    value: string,
    label: string
}

const RateplansComponent: React.FC<RateplansComponentProps> = ({ control, watch, roomType, errors }) => {
    const [ratePlanOptions, setRatePlanOptions] = useState<Rateplan[]>([]);

    useEffect(() => {
        if (watch(`room_type_id`) !== '' || watch(`room_type_id`) !== undefined) {
            const roomTypeId = watch(`room_type_id`);
            const room = roomType;
            const roomData = room.data.find((room: RoomTypeData) => room.id === roomTypeId);
            if (roomData) {
                const ratePlans = roomData.rate_plans;
                if (ratePlans) {
                    setRatePlanOptions(ratePlans.map((rate: { id: string, name: string }) => ({
                        value: rate.id,
                        label: rate.name
                    }))
                    )
                }
            }
        }
    }, [watch(`room_type_id`)])

    return (
        <div className="col-12">
            <label className="col-form-label" htmlFor="rateplans">Rateplans:</label>
            <Controller
                control={control}
                name={`rate_plan_id`}
                render={({ field }) => (
                    <Select
                        className={clsx('', { 'is-invalid': errors.rate_plan_id })}
                        options={ratePlanOptions}
                        value={ratePlanOptions.find((rate) => rate.value === field.value)}
                        onChange={(selectedOption) => {
                            if (selectedOption) {
                                field.onChange(selectedOption.value);
                            }
                        }}
                        noOptionsMessage={() => 'No rate plans available'}
                    />
                )}
            />
            {errors.rate_plan_id && (
                <div className="invalid-feedback">
                    {errors.rate_plan_id.message}
                </div>
            )}
        </div>
    )
}